/* AUTOControl — interacciones de marca. JS nativo, sin dependencias. */
(() => {
  const doc = document.documentElement;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 990px)');
  doc.classList.add('ac-ready');

  /* ---------- Header: altura, estado al hacer scroll y menú móvil ---------- */
  const initHeader = () => {
    const header = document.querySelector('[data-ac-header]');
    if (!header) return;

    const setHeight = () => document.body.style.setProperty('--header-height', `${header.offsetHeight}px`);
    setHeight();
    if ('ResizeObserver' in window) new ResizeObserver(setHeight).observe(header);

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        header.dataset.scrolled = String(window.scrollY > 8);
        ticking = false;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    const toggle = header.querySelector('[data-ac-menu-toggle]');
    const menu = header.querySelector('[data-ac-menu]');
    if (!toggle || !menu || toggle.dataset.bound) return;
    toggle.dataset.bound = 'true';

    const setOpen = (open) => {
      toggle.setAttribute('aria-expanded', String(open));
      toggle.querySelector('[data-ac-menu-label]').textContent = open ? toggle.dataset.close : toggle.dataset.open;
      menu.classList.toggle('is-open', open);
      menu.toggleAttribute('inert', !open);
      document.body.style.overflow = open ? 'hidden' : '';
      if (open) menu.querySelector('a')?.focus({ preventScroll: true });
    };
    toggle.addEventListener('click', () => setOpen(toggle.getAttribute('aria-expanded') !== 'true'));
    menu.addEventListener('click', (e) => { if (e.target.closest('a')) setOpen(false); });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') { setOpen(false); toggle.focus(); }
    });
    desktop.addEventListener('change', (e) => { if (e.matches) setOpen(false); });
  };

  /* ---------- Reveal al hacer scroll ---------- */
  let revealObserver;
  const initReveal = (root = document) => {
    const items = root.querySelectorAll('[data-ac-reveal]:not(.is-in), [data-ac-zoom]:not(.is-in), [data-ac-live]:not(.is-live)');
    if (!items.length) return;
    if (reduced.matches || !('IntersectionObserver' in window)) {
      items.forEach((el) => { el.classList.add('is-in'); if (el.hasAttribute('data-ac-live')) startReadout(el); });
      return;
    }
    revealObserver ||= new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        el.classList.add('is-in');
        if (el.hasAttribute('data-ac-live')) startReadout(el);
        revealObserver.unobserve(el);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    items.forEach((el) => revealObserver.observe(el));
  };

  /* ---------- Parallax sutil (solo escritorio, solo en viewport) ---------- */
  const parallax = new Set();
  let parallaxRaf = 0;
  const renderParallax = () => {
    parallaxRaf = 0;
    const vh = window.innerHeight;
    parallax.forEach((el) => {
      const rect = el.parentElement.getBoundingClientRect();
      const progress = Math.max(-1, Math.min(1, (rect.top + rect.height / 2 - vh / 2) / vh));
      const amount = parseFloat(el.dataset.acParallax) || 0.05;
      const shift = progress * amount * rect.height * 0.5;
      el.style.transform = `translate3d(0, ${shift.toFixed(1)}px, 0) scale(${(1 + amount).toFixed(3)})`;
    });
  };
  const requestParallax = () => { if (!parallaxRaf) parallaxRaf = requestAnimationFrame(renderParallax); };
  const initParallax = (root = document) => {
    if (reduced.matches || !desktop.matches || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => (e.isIntersecting ? parallax.add(e.target) : parallax.delete(e.target)));
      requestParallax();
    });
    root.querySelectorAll('[data-ac-parallax]').forEach((el) => io.observe(el));
  };
  window.addEventListener('scroll', requestParallax, { passive: true });
  window.addEventListener('resize', requestParallax, { passive: true });

  /* ---------- Lectura de las cuatro ruedas ---------- */
  const formatBar = (v) => v.toFixed(1).replace('.', ',');
  const formatPsi = (v) => String(Math.round(v * 14.5038));

  const setUnits = (section, unit) => {
    section.dataset.unit = unit;
    section.querySelectorAll('[data-ac-unit]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.acUnit === unit)));
    section.querySelectorAll('[data-ac-wheel]').forEach((wheel) => {
      const num = wheel.querySelector('[data-ac-num]');
      const unitEl = wheel.querySelector('[data-ac-unit-label]');
      const bar = parseFloat(wheel.classList.contains('is-alert') ? wheel.dataset.alertBar : wheel.dataset.bar);
      num.textContent = unit === 'psi' ? formatPsi(bar) : formatBar(bar);
      unitEl.textContent = unit === 'psi' ? 'psi' : 'bar';
    });
  };

  function startReadout(section) {
    section.classList.add('is-live');
    if (section.dataset.started) return;
    section.dataset.started = 'true';
    const alertWheel = section.querySelector('[data-ac-wheel][data-alert-bar]');
    if (!alertWheel) return;
    const trigger = () => {
      alertWheel.classList.add('is-alert');
      section.querySelector(`[data-ac-tyre="${alertWheel.dataset.pos}"]`)?.classList.add('is-alert');
      setUnits(section, section.dataset.unit || 'bar');
    };
    reduced.matches ? trigger() : setTimeout(trigger, 2200);
  }

  const initReadout = (root = document) => {
    root.querySelectorAll('[data-ac-live]').forEach((section) => {
      section.querySelectorAll('[data-ac-unit]').forEach((btn) => {
        if (btn.dataset.bound) return;
        btn.dataset.bound = 'true';
        btn.addEventListener('click', () => setUnits(section, btn.dataset.acUnit));
      });
    });
  };

  /* ---------- Arranque ---------- */
  const init = (root = document) => {
    initReveal(root);
    initParallax(root);
    initReadout(root);
  };

  initHeader();
  init();

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('[data-ac-header]')) initHeader();
    init(e.target);
  });
})();
